import React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Chip, Heading } from '@frontend/design-system';
import { Maximum } from '../../types/insurance-report-api.types';
import { isNotUndefinedOrNull, showDollarSign, titleCase } from '../../utils/utils';
import { Accordion } from '../common-components/accordion';
import { SectionInfo } from '../common-components/sectionInfo';

type MaximumsProps = {
  maximums: Maximum[];
};

export const Maximums = React.memo(({ maximums }: MaximumsProps) => {
  const { t } = useTranslation('insurance-eligibility-report');

  if (!maximums || maximums.length === 0) {
    return null; // Return null if there are no maximums to display
  }

  return (
    <Accordion accordionId='maximums' accordionTitle='Maximums'>
      {maximums.map((item, index) => (
        <div key={index}>
          <Heading level={3} css={categoryStyles}>
            {titleCase(item.category || '', '_')}
          </Heading>
          <div css={gridStyles}>
            {isNotUndefinedOrNull(item.remaining) && (
              <SectionInfo label={t('Remaining')} value={showDollarSign(item.remaining)} />
            )}
            {isNotUndefinedOrNull(item.amount) && (
              <SectionInfo label={t('Total')} value={showDollarSign(item.amount)} />
            )}
            {item.coverageLevel && (
              <Chip.Tag color='blue' css={{ padding: theme.spacing(1), width: 'fit-content' }} size='small'>
                {titleCase(item.coverageLevel || '')}
              </Chip.Tag>
            )}
          </div>
        </div>
      ))}
    </Accordion>
  );
});

Maximums.displayName = 'Maximums';

const gridStyles = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  row-gap: ${theme.spacing(4)};
  column-gap: ${theme.spacing(4)};
  align-items: center;
  margin-bottom: ${theme.spacing(2)};
`;

const categoryStyles = css`
  margin-bottom: ${theme.spacing(2)};
  color: ${theme.colors.neutral50};
`;
